// screens/LoginScreen.js

import React, { useState, useEffect, useRef } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import useTranslation from "../i18n/useTranslation";
import logoImg from "../img/logo.png";
import onboardingImg from "../img/onboarding.png";
import "./LoginScreen.css";

const IconGoogle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const SMS_RESEND_COOLDOWN = 60;

const LoginScreen = ({ authHook }) => {
  const { t } = useTranslation();

  const {
    loginWithGoogle, sendSms, verifySmsCode, initRecaptcha,
    loading, error, smsLoading, smsError, user, phoneVerified,
  } = authHook;

  const [step, setStep]                     = useState("google");
  const [phone, setPhone]                   = useState("");
  const [phoneValid, setPhoneValid]         = useState(false);
  const [code, setCode]                     = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef                        = useRef(null);
  const cooldownRef                         = useRef(null);

  useEffect(() => { if (user && !phoneVerified) setStep("sms_input"); }, [user, phoneVerified]);
  useEffect(() => { if (step === "sms_input") initRecaptcha("recaptcha-container"); }, [step, initRecaptcha]);
  useEffect(() => { if (step === "sms_code") setTimeout(() => codeInputRef.current?.focus(), 150); }, [step]);
  useEffect(() => () => clearInterval(cooldownRef.current), []);

  const handlePhoneChange = (value) => {
    setPhone(value || "");
    setPhoneValid(value ? isValidPhoneNumber(value) : false);
  };

  const startCooldown = () => {
    setResendCooldown(SMS_RESEND_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => { if (s <= 1) { clearInterval(cooldownRef.current); return 0; } return s - 1; });
    }, 1000);
  };

  const handleGoogle     = async () => { await loginWithGoogle(); };
  const handleSendSms    = async () => { if (!phoneValid) return; const r = await sendSms(phone); if (r.success) { setStep("sms_code"); startCooldown(); } };
  const handleVerifyCode = async () => { if (code.length !== 6) return; await verifySmsCode(code); };
  const handleResend     = async () => { if (resendCooldown > 0) return; const r = await sendSms(phone); if (r.success) { setCode(""); startCooldown(); } };

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
    if (val.length === 6) setTimeout(() => verifySmsCode(val), 100);
  };

  return (
    <div className="login-screen">
      <div id="recaptcha-container" />

      <div className="login-screen__card">

        {/* Logo — envuelto en su propio contexto de composición */}
        <div className="login-screen__logo-wrap">
          <img src={logoImg} alt="PrizeReal" className="login-screen__logo-img" />
        </div>

        {/* ── Paso 1: Google ─────────────────────────────────────────── */}
        {step === "google" && (
          <>
            <img
              src={onboardingImg}
              alt=""
              className="login-screen__onboarding-img"
              aria-hidden="true"
            />

            <div className="login-screen__step">
              <h1 className="login-screen__title">{t("auth.welcomeTitle")}</h1>
              <p className="login-screen__subtitle">{t("auth.welcomeSubtitle")}</p>

              <div className="login-screen__steps" aria-hidden="true">
                {[
                  "Compartí un local con tus contactos",
                  "Acumulá clicks reales de tus amigos",
                  "Presentate en el local y cobrá tu premio",
                ].map((text, i) => (
                  <div key={i} className="login-screen__step-item">
                    <span className="login-screen__step-num">{i + 1}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>

              <button
                className="login-screen__btn login-screen__btn--google"
                onClick={handleGoogle}
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? <span className="login-screen__spinner" aria-hidden="true" /> : <IconGoogle />}
                {loading ? t("auth.signInLoading") : t("auth.signInGoogle")}
              </button>

              {error && <p className="login-screen__error" role="alert">{t(`auth.errors.${error}`) || t("app.error")}</p>}
            </div>
          </>
        )}

        {/* ── Paso 2a: Número de teléfono ───────────────────────────── */}
        {step === "sms_input" && (
          <div className="login-screen__step">
            <h2 className="login-screen__title">{t("auth.smsTitle")}</h2>
            <p className="login-screen__subtitle">{t("auth.smsRequired")}</p>

            <div className="login-screen__phone-wrap">
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="AR"
                value={phone}
                onChange={handlePhoneChange}
                className="login-screen__phone-input"
                numberInputProps={{ className: "login-screen__phone-number", placeholder: "Tu número", autoComplete: "tel", inputMode: "tel" }}
                onKeyDown={(e) => e.key === "Enter" && handleSendSms()}
              />
            </div>

            {phone && (
              <p className={`login-screen__phone-hint ${phoneValid ? "login-screen__phone-hint--ok" : "login-screen__phone-hint--error"}`}>
                {phoneValid ? "✓ Número válido" : "Formato incompleto — incluí el código de área"}
              </p>
            )}

            <button className="login-screen__btn login-screen__btn--primary" onClick={handleSendSms} disabled={smsLoading || !phoneValid} aria-busy={smsLoading}>
              {smsLoading && <span className="login-screen__spinner" aria-hidden="true" />}
              {t("auth.smsSend")}
            </button>

            {smsError && <p className="login-screen__error" role="alert">{t("auth.errors.phoneFailed")}</p>}
          </div>
        )}

        {/* ── Paso 2b: Código SMS ────────────────────────────────────── */}
        {step === "sms_code" && (
          <div className="login-screen__step">
            <h2 className="login-screen__title">{t("auth.smsTitle")}</h2>
            <p className="login-screen__subtitle">{t("auth.smsSubtitle", { phone })}</p>

            <input
              ref={codeInputRef}
              className="login-screen__code-input"
              type="tel" inputMode="numeric" pattern="[0-9]*"
              maxLength={6} placeholder="000000"
              value={code} onChange={handleCodeChange}
              aria-label={t("auth.smsCodeLabel")}
              autoComplete="one-time-code"
            />

            <button className="login-screen__btn login-screen__btn--primary" onClick={handleVerifyCode} disabled={smsLoading || code.length !== 6} aria-busy={smsLoading}>
              {smsLoading && <span className="login-screen__spinner" aria-hidden="true" />}
              {t("auth.smsVerify")}
            </button>

            <button className="login-screen__btn-link" onClick={handleResend} disabled={resendCooldown > 0}>
              {resendCooldown > 0 ? t("auth.smsResendIn", { seconds: resendCooldown }) : t("auth.smsResend")}
            </button>

            <button className="login-screen__btn-link" onClick={() => { setStep("sms_input"); setCode(""); }}>
              Cambiar número
            </button>

            {smsError && <p className="login-screen__error" role="alert">{t("auth.smsError")}</p>}
          </div>
        )}

      </div>
    </div>
  );
};

export default LoginScreen;